/**
 * Analytics Hook
 * Custom hook for report analytics and insights
 */

import { useState, useCallback, useEffect } from 'react';
import AnalyticsService from '../services/AnalyticsService';

export const useAnalytics = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  const analyzeReport = useCallback(async (reportType, data, parameters = {}) => {
    try {
      setAnalyzing(true);
      setError(null);

      const result = await AnalyticsService.analyzeReportData(reportType, data, parameters);
      
      setAnalytics(result);
      setFromCache(result.fromCache || false);
      
      return result;
    } catch (err) {
      console.error('Analytics analysis failed:', err);
      setError(err.message);
      return null;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const clearAnalytics = useCallback(() => {
    setAnalytics(null);
    setError(null);
    setFromCache(false);
  }, []);

  const clearCache = useCallback(() => {
    AnalyticsService.clearCache();
  }, []);

  const getCacheStats = useCallback(() => {
    return AnalyticsService.getCacheStats();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    analyzing,
    analytics,
    error,
    fromCache,

    // Actions
    analyzeReport,
    clearAnalytics,
    clearCache,
    clearError,

    // Utilities
    getCacheStats,

    // Status helpers
    hasAnalytics: !!analytics,
    hasError: !!error,
    hasInsights: analytics?.insights?.insights?.length > 0,
    hasTrends: analytics?.insights?.trends?.length > 0,
    hasRecommendations: analytics?.insights?.recommendations?.length > 0
  };
};

// Specialized hook for insights display
export const useInsights = (analytics) => {
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [filteredInsights, setFilteredInsights] = useState([]);

  useEffect(() => {
    if (analytics?.insights?.insights) {
      setFilteredInsights(analytics.insights.insights);
    }
  }, [analytics]);

  const filterByImpact = useCallback((impactLevel) => {
    if (!analytics?.insights?.insights) return;
    
    if (impactLevel === 'all') {
      setFilteredInsights(analytics.insights.insights);
    } else {
      setFilteredInsights(
        analytics.insights.insights.filter(insight => insight.impact === impactLevel)
      );
    }
  }, [analytics]);

  const sortInsights = useCallback((sortBy = 'impact') => {
    if (!filteredInsights.length) return;

    const sorted = [...filteredInsights].sort((a, b) => {
      switch (sortBy) {
        case 'impact':
          const impactOrder = { high: 3, medium: 2, low: 1 };
          return (impactOrder[b.impact] || 0) - (impactOrder[a.impact] || 0);
        case 'type':
          return a.type.localeCompare(b.type);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredInsights(sorted);
  }, [filteredInsights]);

  const selectInsight = useCallback((insight) => {
    setSelectedInsight(insight);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedInsight(null);
  }, []);

  const getInsightsByType = useCallback((type) => {
    return filteredInsights.filter(insight => insight.type === type);
  }, [filteredInsights]);

  return {
    // State
    selectedInsight,
    filteredInsights,

    // Actions
    filterByImpact,
    sortInsights,
    selectInsight,
    clearSelection,

    // Utilities
    getInsightsByType,

    // Status
    hasSelection: !!selectedInsight,
    insightCount: filteredInsights.length,
    highImpactCount: filteredInsights.filter(i => i.impact === 'high').length,
    mediumImpactCount: filteredInsights.filter(i => i.impact === 'medium').length,
    lowImpactCount: filteredInsights.filter(i => i.impact === 'low').length
  };
};

// Hook for trends analysis
export const useTrends = (analytics) => {
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [trendFilter, setTrendFilter] = useState('all');

  const trends = analytics?.insights?.trends || [];

  const filteredTrends = trends.filter(trend => {
    if (trendFilter === 'all') return true;
    return trend.direction === trendFilter;
  });

  const selectTrend = useCallback((trend) => {
    setSelectedTrend(trend);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTrend(null);
  }, []);

  const filterTrends = useCallback((direction) => {
    setTrendFilter(direction);
    setSelectedTrend(null);
  }, []);

  const getTrendsByType = useCallback((type) => {
    return filteredTrends.filter(trend => trend.type === type);
  }, [filteredTrends]);

  return {
    // State
    selectedTrend,
    filteredTrends,
    trendFilter,

    // Actions
    selectTrend,
    clearSelection,
    filterTrends,

    // Utilities
    getTrendsByType,

    // Status
    hasSelection: !!selectedTrend,
    trendCount: filteredTrends.length,
    upTrends: filteredTrends.filter(t => t.direction === 'up').length,
    downTrends: filteredTrends.filter(t => t.direction === 'down').length,
    neutralTrends: filteredTrends.filter(t => t.direction === 'neutral').length
  };
};

// Hook for recommendations management
export const useRecommendations = (analytics) => {
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const recommendations = analytics?.insights?.recommendations || [];

  const filteredRecommendations = recommendations.filter(rec => {
    const priorityMatch = priorityFilter === 'all' || rec.priority === priorityFilter;
    const categoryMatch = categoryFilter === 'all' || rec.category === categoryFilter;
    return priorityMatch && categoryMatch;
  });

  const selectRecommendation = useCallback((recommendation) => {
    setSelectedRecommendation(recommendation);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRecommendation(null);
  }, []);

  const filterByPriority = useCallback((priority) => {
    setPriorityFilter(priority);
    setSelectedRecommendation(null);
  }, []);

  const filterByCategory = useCallback((category) => {
    setCategoryFilter(category);
    setSelectedRecommendation(null);
  }, []);

  const getRecommendationsByCategory = useCallback((category) => {
    return filteredRecommendations.filter(rec => rec.category === category);
  }, [filteredRecommendations]);

  const sortRecommendations = useCallback((sortBy = 'priority') => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    
    return [...filteredRecommendations].sort((a, b) => {
      if (sortBy === 'priority') {
        return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      }
      if (sortBy === 'category') {
        return a.category.localeCompare(b.category);
      }
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
  }, [filteredRecommendations]);

  return {
    // State
    selectedRecommendation,
    filteredRecommendations,
    priorityFilter,
    categoryFilter,

    // Actions
    selectRecommendation,
    clearSelection,
    filterByPriority,
    filterByCategory,

    // Utilities
    getRecommendationsByCategory,
    sortRecommendations,

    // Status
    hasSelection: !!selectedRecommendation,
    recommendationCount: filteredRecommendations.length,
    highPriorityCount: filteredRecommendations.filter(r => r.priority === 'high').length,
    mediumPriorityCount: filteredRecommendations.filter(r => r.priority === 'medium').length,
    lowPriorityCount: filteredRecommendations.filter(r => r.priority === 'low').length
  };
};